import React, { useState } from "react";
import { Alert, Box, Button, Divider, Paper, Typography } from "@mui/material";
import { Geometry } from "geojson";
import { noop } from "lodash";

import { selectLayerByVersionAndLayerKey } from "fond/api/layersSlice";
import { useCurrentProject } from "fond/hooks/useCurrentProject";
import Upload from "fond/project/panels/upload/Upload";
import UploadProgress from "fond/project/panels/upload/UploadProgress";
import { beginLayerUpload, dismissUploadError } from "fond/project/redux";
import { addImport } from "fond/redux/imports";
import { LayerId, MuPowerEditableLayerId, MuPowerSolverLayerId } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";
import { isPlannerProject } from "fond/utils/project";
import { ConfirmModal, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

export interface MultiLayerUploadSection {
  layerId: MuPowerEditableLayerId | MuPowerSolverLayerId | LayerId;
  displayName: string;
  geometryType: Geometry["type"];
}

const UploadSection: React.FC<MultiLayerUploadSection> = ({ layerId, displayName, geometryType }) => {
  const dispatch = useAppDispatch();
  const project = useCurrentProject();
  const isPlanner = isPlannerProject(project);
  const projectId = useAppSelector((state) => state.project.projectId);
  const versionId = useAppSelector((state) => state.project.versionId);
  const existingLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId }));
  const hasExistingData = !!existingLayer;
  const importState = useAppSelector((state) => state.imports[versionId]?.[layerId]);
  const plannerUpload = useAppSelector((state) => state.project.projects[projectId]?.uploads?.[layerId]);
  const uploading = isPlanner ? plannerUpload != null : isImportInProgress(importState?.status);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const proceedWithUpload = () => {
    if (isPlanner) {
      // Planner(extended) projects always upload directly to the version via `/versions/{versionId}/layers`,
      // which applies planner-specific server-side handling (e.g. a street layer gets segmented).
      dispatch(
        beginLayerUpload(projectId, versionId, layerId, { [layerId]: selectedFiles }, false, undefined, () => dispatch(dismissUploadError(layerId)))
      );
    } else {
      // Other project type uses the generic create-layer-then-import flow, which builds vector tiles that
      // planner projects don't use.
      dispatch(
        addImport({
          versionId,
          layerKey: layerId,
          displayName,
          files: selectedFiles,
        })
      );
    }

    setSelectedFiles([]);
    setShowConfirmModal(false);
  };

  const handleOnUpload = () => {
    if (hasExistingData) {
      setShowConfirmModal(true);
    } else {
      proceedWithUpload();
    }
  };

  const renderUploadState = () => {
    // Planner uploads show a genuine progress bar
    if (isPlanner && plannerUpload) {
      return <UploadProgress upload={plannerUpload} inputLayer={layerId} onDismissUploadError={() => dispatch(dismissUploadError(layerId))} />;
    }

    if (uploading) {
      return (
        <>
          <Alert severity="info" sx={{ my: 1 }}>
            FOND is now processing your data, and it will be visible on the map shortly.
          </Alert>
          <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
            <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
          </Paper>
        </>
      );
    }

    return (
      <>
        <Upload
          layer={{
            id: layerId,
            geometryType,
            name: displayName,
          }}
          onSelectFiles={(validFiles) => setSelectedFiles(validFiles)}
          autoUpload={false}
          onBeginUpload={noop}
          onRemove={() => setSelectedFiles([])}
          includeLayerName={false}
          convertToDualSidedField={null}
        />
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Button
            variant="contained"
            size="small"
            disabled={selectedFiles.length === 0}
            onClick={handleOnUpload}
            sx={{ px: 2 }}
            data-testid={`upload-${layerId}-button`}
          >
            Upload
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box mb={2}>
      <Typography fontWeight="500">{displayName}</Typography>
      <ConfirmModal
        open={showConfirmModal}
        header="Replace existing data?"
        confirmText="Continue"
        cancelText="Cancel"
        color="error"
        content={
          <Typography variant="body2">This will replace all existing {displayName.toLowerCase()} data. This action cannot be undone.</Typography>
        }
        onConfirm={proceedWithUpload}
        onCancel={() => setShowConfirmModal(false)}
      />
      {renderUploadState()}
    </Box>
  );
};

interface MultiLayerUploadFormProps {
  title: string;
  sections: MultiLayerUploadSection[];
}

const MultiLayerUploadForm: React.FC<MultiLayerUploadFormProps> = ({ title, sections }) => {
  const { goBack } = useStackedNavigationContext();

  return (
    <Box>
      <StackedNavigationHeader title={title} onBackButtonClick={() => goBack()} />
      <Box px={1}>
        {sections.map((section, index) => (
          <React.Fragment key={section.layerId}>
            {index > 0 && <Divider sx={{ my: 1 }} />}
            <UploadSection {...section} />
          </React.Fragment>
        ))}
        <Divider sx={{ my: 2 }} />
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Button color="primary" size="small" onClick={() => goBack()} data-testid="multi-layer-upload-done">
            Done
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MultiLayerUploadForm;
